// components/StatisticsDashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  
  Tooltip,
  
  Alert,
  Snackbar,
  Tabs,
  Tab,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  AppBar,
} from '@mui/material';
import {
  Restaurant,
  Hotel,
  Build,
  AttachMoney,
  People,
  TrendingUp,
  CalendarToday,
  Refresh,
  Download,
  Print,
  Visibility,
  RoomService,
  LocalBar,
  Receipt,
  Pool,
  FitnessCenter,
  Spa,
  BusinessCenter,
 
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../../components/sidebars/Sidebar_hotelAdmin';
import TopNav from '../../../components/TopNav_hotelAdmin';

// Styled Components
const DashboardCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const StatCard = styled(Card)(({ theme, color = 'primary' }) => ({
  borderLeft: `4px solid ${theme.palette[color].main}`,
  borderRadius: '8px',
  height: '100%',
}));

const ProgressBar = styled(LinearProgress)(({ theme, value }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[200],
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
    backgroundColor: value > 80 ? theme.palette.success.main : 
                   value > 60 ? theme.palette.warning.main : 
                   theme.palette.primary.main,
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return theme.palette.success.main;
      case 'paid': return theme.palette.success.main;
      case 'pending': return theme.palette.warning.main;
      case 'confirmed': return theme.palette.info.main;
      case 'preparing': return theme.palette.primary.main;
      case 'ready': return theme.palette.success.light;
      case 'served': return theme.palette.success.dark;
      case 'cancelled': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  return {
    backgroundColor: getStatusColor(status),
    color: 'white',
    fontWeight: 'bold',
    fontSize: '12px',
  };
});

const UnitIcon = ({ unit, size = 'large' }) => {
  const icons = {
    restaurant_bar: <Restaurant fontSize={size} />,
    reception: <Hotel fontSize={size} />,
    facility_management: <Build fontSize={size} />,
  };
  return icons[unit] || <AttachMoney fontSize={size} />;
};

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  minWidth: 120,
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
  },
}));

const ReviewCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

// Helper functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN'
  }).format(amount || 0);
};

const formatNumber = (number) => {
  return new Intl.NumberFormat().format(number || 0);
};

// Tab Components for each unit
const UnitSummaryTab = ({ unit, data }) => {
  const renderUnitSummary = () => {
    switch (unit) {
      case 'restaurant_bar':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Order Types Distribution</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Count</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                          <TableCell align="right">Percentage</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.order_types?.map((type, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {type.order_type === 'dine_in' && <Restaurant />}
                                {type.order_type === 'room_service' && <RoomService />}
                                {type.order_type === 'takeaway' && <LocalBar />}
                                {type.order_type === 'delivery' && <LocalBar />}
                                {type.order_type.replace('_', ' ').toUpperCase()}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{formatNumber(type.order_count)}</TableCell>
                            <TableCell align="right">{formatCurrency(type.revenue)}</TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={`${((type.order_count / data.summary?.total_orders) * 100).toFixed(1)}%`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Top Performing Items</Typography>
                    <Chip label="By Revenue" color="primary" size="small" />
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell align="right">Qty Sold</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.top_items?.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {item.name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {item.order_count} orders
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={formatNumber(item.quantity_sold)} 
                                size="small" 
                                color="secondary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold" color="primary.main">
                                {formatCurrency(item.total_revenue)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Staff Performance */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Staff Performance</Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Staff Member</TableCell>
                          <TableCell align="right">Orders Handled</TableCell>
                          <TableCell align="right">Revenue Generated</TableCell>
                          <TableCell align="right">Performance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.staff_performance?.map((staff, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <People color="action" />
                                <Typography variant="body2">
                                  {staff.first_name} {staff.other_names}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={formatNumber(staff.orders_handled)} 
                                size="small" 
                                color="info"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">
                                {formatCurrency(staff.revenue_generated)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <LinearProgress 
                                variant="determinate" 
                                value={(staff.orders_handled / Math.max(...data.staff_performance.map(s => s.orders_handled))) * 100}
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 'reception':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Booking Sources</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Source</TableCell>
                          <TableCell align="right">Bookings</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                          <TableCell align="right">Market Share</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.booking_sources?.map((source, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <StatusChip 
                                label={source.source.toUpperCase()} 
                                status={source.source === 'website' ? 'completed' : 'confirmed'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">{formatNumber(source.booking_count)}</TableCell>
                            <TableCell align="right">{formatCurrency(source.total_amount)}</TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={`${((source.booking_count / data.summary?.total_bookings) * 100).toFixed(1)}%`}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Payment Methods</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Method</TableCell>
                          <TableCell align="right">Transactions</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right">Usage</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.payment_methods?.map((method, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Receipt color="action" />
                                {method.payment_method}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{formatNumber(method.payment_count)}</TableCell>
                            <TableCell align="right">{formatCurrency(method.total_amount)}</TableCell>
                            <TableCell align="right">
                              <LinearProgress 
                                variant="determinate" 
                                value={(method.payment_count / data.payment_methods.reduce((sum, m) => sum + m.payment_count, 0)) * 100}
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Room Occupancy Stats */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Room Occupancy</Typography>
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography>Occupied Rooms</Typography>
                      <Chip 
                        label={`${data.occupancy?.occupied_rooms}/${data.occupancy?.total_rooms}`}
                        color="success"
                        size="small"
                      />
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(data.occupancy?.occupied_rooms / data.occupancy?.total_rooms) * 100}
                      sx={{ height: 10, borderRadius: 5, mb: 2 }}
                    />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption">Available</Typography>
                      <Typography variant="caption" fontWeight="bold">
                        {data.occupancy?.available_rooms} rooms
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption">Maintenance</Typography>
                      <Typography variant="caption" color="warning.main" fontWeight="bold">
                        {data.occupancy?.maintenance_rooms} rooms
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Guest Demographics */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Guest Demographics</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Nationality</TableCell>
                          <TableCell align="right">Guests</TableCell>
                          <TableCell align="right">Percentage</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.guest_demographics?.map((demo, index) => (
                          <TableRow key={index}>
                            <TableCell>{demo.nationality}</TableCell>
                            <TableCell align="right">{formatNumber(demo.guest_count)}</TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={`${((demo.guest_count / data.guest_demographics.reduce((sum, g) => sum + g.guest_count, 0)) * 100).toFixed(1)}%`}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 'facility_management':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Facility Categories</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Category</TableCell>
                          <TableCell align="right">Bookings</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                          <TableCell align="right">Performance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.facility_categories?.map((category, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {category.category === 'swimming_pool' && <Pool />}
                                {category.category === 'gym' && <FitnessCenter />}
                                {category.category === 'spa' && <Spa />}
                                {category.category === 'conference_room' && <BusinessCenter />}
                                {category.category.replace('_', ' ').toUpperCase()}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{formatNumber(category.booking_count)}</TableCell>
                            <TableCell align="right">{formatCurrency(category.total_revenue)}</TableCell>
                            <TableCell align="right">
                              <LinearProgress 
                                variant="determinate" 
                                value={(category.total_revenue / Math.max(...data.facility_categories.map(c => c.total_revenue))) * 100}
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Popular Facilities</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Facility</TableCell>
                          <TableCell align="right">Bookings</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.popular_facilities?.map((facility, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {facility.facility_name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={formatNumber(facility.booking_count)} 
                                size="small" 
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold" color="primary.main">
                                {formatCurrency(facility.total_revenue)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
            
                       {/* Inventory Status */}
            
          </Grid>
        );

      default:
        return null;
    }
  };

  return renderUnitSummary();
};

// Fixed UnitTransactionsTab Component
const UnitTransactionsTab = ({ unit, selectedDate }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const getHotelCode = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.hotelcode || storedProfile.hotel_code || '';
    } catch (error) {
      console.error('Error getting hotel code:', error);
      return '';
    }
  };

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const loadTransactions = async () => {
    setLoading(true);
    setError('');
    try {
      const hotelCode = getHotelCode();
      if (!hotelCode) {
        throw new Error('Hotel code not found');
      }

      const response = await axios.get(`${API_BASE_URL}/hotel/admin/unit_details.php`, {
        params: {
          hotel_code: hotelCode,
          unit: unit,
          date: selectedDate,
          page: page,
          limit: limit
        }
      });

      if (response.data.success) {
        setTransactions(response.data.transactions);
      } else {
        throw new Error(response.data.error || 'Failed to load transactions');
      }
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (unit && selectedDate) {
      loadTransactions();
    }
  }, [unit, selectedDate, page]);

  const renderTransactionTable = () => {
    switch (unit) {
      case 'restaurant_bar':
        return (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order Number</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Table</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Staff</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {transaction.order_number}
                      </Typography>
                    </TableCell>
                    <TableCell>{transaction.customer_name || 'Walk-in'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={transaction.order_type?.replace('_', ' ').toUpperCase() || 'N/A'} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {transaction.table_number ? `Table ${transaction.table_number}` : 'Room Service'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        {formatCurrency(transaction.final_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={transaction.status} label={transaction.status?.toUpperCase()} />
                    </TableCell>
                    <TableCell>{transaction.staff_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(transaction.created_at).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case 'reception':
        return (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Booking Number</TableCell>
                  <TableCell>Guest</TableCell>
                  <TableCell>Room</TableCell>
                  <TableCell>Check-in</TableCell>
                  <TableCell>Check-out</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment Method</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {transaction.booking_number}
                      </Typography>
                    </TableCell>
                    <TableCell>{transaction.guest_name}</TableCell>
                    <TableCell>
                      <Chip label={transaction.room_name} size="small" color="secondary" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(transaction.check_in_date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(transaction.check_out_date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(transaction.total_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color={transaction.paid_amount >= transaction.total_amount ? "success.main" : "warning.main"}
                        fontWeight="bold"
                      >
                        {formatCurrency(transaction.paid_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={transaction.booking_status} label={transaction.booking_status?.toUpperCase()} />
                    </TableCell>
                    <TableCell>
                      {transaction.payment_method ? (
                        <Chip 
                          label={transaction.payment_method} 
                          size="small" 
                          variant="outlined"
                        />
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case 'facility_management':
        return (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Booking Number</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell>Guest</TableCell>
                  <TableCell>Booking Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment Method</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {transaction.booking_number}
                      </Typography>
                    </TableCell>
                    <TableCell>{transaction.facility_name}</TableCell>
                    <TableCell>{transaction.guest_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(transaction.booking_date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.booking_time}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(transaction.total_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color={transaction.paid_amount >= transaction.total_amount ? "success.main" : "warning.main"}
                        fontWeight="bold"
                      >
                        {formatCurrency(transaction.paid_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={transaction.booking_status} label={transaction.booking_status?.toUpperCase()} />
                    </TableCell>
                    <TableCell>
                      {transaction.payment_method ? (
                        <Chip 
                          label={transaction.payment_method} 
                          size="small" 
                          variant="outlined"
                        />
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading transactions...
        </Typography>
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Recent Transactions - {unit.replace('_', ' ').toUpperCase()}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button 
              variant="outlined" 
              startIcon={<Refresh />}
              onClick={loadTransactions}
              disabled={loading}
            >
              Refresh
            </Button>
            <Chip 
              label={`${transactions.length} transactions`} 
              color="primary" 
              variant="outlined"
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {transactions.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Receipt sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              No transactions found for {selectedDate}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Try selecting a different date or check if there were any activities.
            </Typography>
          </Box>
        ) : (
          renderTransactionTable()
        )}

        {/* Pagination */}
        {transactions.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Showing {transactions.length} transactions
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={transactions.length < limit}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Fixed UnitAnalyticsTab Component
const UnitAnalyticsTab = ({ unit, data }) => {
  const renderAnalyticsContent = () => {
    switch (unit) {
      case 'restaurant_bar':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Hourly Order Trends</Typography>
                  {data.hourly_trends && data.hourly_trends.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Hour</TableCell>
                            <TableCell align="right">Orders</TableCell>
                            <TableCell align="right">Revenue</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.hourly_trends.map((trend, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                {trend.hour === 0 ? '12 AM' : 
                                 trend.hour < 12 ? `${trend.hour} AM` : 
                                 trend.hour === 12 ? '12 PM' : 
                                 `${trend.hour - 12} PM`}
                              </TableCell>
                              <TableCell align="right">{trend.order_count}</TableCell>
                              <TableCell align="right">{formatCurrency(trend.revenue)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                      No hourly data available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="textSecondary">Completion Rate</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={data.summary?.total_orders ? 
                            (data.summary.completed_orders / data.summary.total_orders * 100) : 0
                          }
                          sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          {data.summary?.total_orders ? 
                            Math.round((data.summary.completed_orders / data.summary.total_orders) * 100) : 0
                          }%
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="textSecondary">Average Order Value</Typography>
                      <Typography variant="h5" color="primary.main">
                        {formatCurrency(data.summary?.avg_order_value || 0)}
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <StatCard>
                          <CardContent sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                              Pending
                            </Typography>
                            <Typography variant="h6" color="warning.main">
                              {formatNumber(data.summary?.pending_orders || 0)}
                            </Typography>
                          </CardContent>
                        </StatCard>
                      </Grid>
                      <Grid item xs={6}>
                        <StatCard>
                          <CardContent sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                              Cancelled
                            </Typography>
                            <Typography variant="h6" color="error.main">
                              {formatNumber(data.summary?.cancelled_orders || 0)}
                            </Typography>
                          </CardContent>
                        </StatCard>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 'reception':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Occupancy Analytics</Typography>
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="textSecondary">Occupancy Rate</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={data.occupancy?.total_rooms ? 
                            (data.occupancy.occupied_rooms / data.occupancy.total_rooms * 100) : 0
                          }
                          sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          {data.occupancy?.total_rooms ? 
                            Math.round((data.occupancy.occupied_rooms / data.occupancy.total_rooms) * 100) : 0
                          }%
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <StatCard>
                          <CardContent sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                              Check-ins
                            </Typography>
                            <Typography variant="h6" color="success.main">
                              {formatNumber(data.summary?.active_stays || 0)}
                            </Typography>
                          </CardContent>
                        </StatCard>
                      </Grid>
                      <Grid item xs={6}>
                        <StatCard>
                          <CardContent sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                              Check-outs
                            </Typography>
                            <Typography variant="h6" color="info.main">
                              {formatNumber(data.summary?.checkouts_today || 0)}
                            </Typography>
                          </CardContent>
                        </StatCard>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Revenue Collection</Typography>
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="textSecondary">Collection Rate</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={data.summary?.total_revenue ? 
                            (data.summary.total_collected / data.summary.total_revenue * 100) : 0
                          }
                          sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          {data.summary?.total_revenue ? 
                            Math.round((data.summary.total_collected / data.summary.total_revenue) * 100) : 0
                          }%
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="textSecondary">Cancellation Rate</Typography>
                      <Typography variant="h6" color="error.main">
                        {data.summary?.total_bookings ? 
                          Math.round((data.summary.cancellations / data.summary.total_bookings) * 100) : 0
                        }%
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 'facility_management':
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Facility Performance</Typography>
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="textSecondary">Revenue per Category</Typography>
                      {data.facility_categories?.map((category, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">
                              {category.category.replace('_', ' ').toUpperCase()}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(category.total_revenue)}
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={(category.total_revenue / Math.max(...data.facility_categories.map(c => c.total_revenue))) * 100}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Facility Health</Typography>
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="textSecondary">Facilities Availability</Typography>
                      <Typography variant="h4" color="success.main">
                        {data.maintenance?.total_rooms ? 
                          Math.round(((data.maintenance.total_rooms - data.maintenance.under_maintenance - data.maintenance.being_cleaned) / data.maintenance.total_rooms) * 100) : 0
                        }%
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <StatCard>
                          <CardContent sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                              Under Maintenance
                            </Typography>
                            <Typography variant="h6" color="warning.main">
                              {formatNumber(data.maintenance?.under_maintenance || 0)}
                            </Typography>
                          </CardContent>
                        </StatCard>
                      </Grid>
                      <Grid item xs={6}>
                        <StatCard>
                          <CardContent sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                              Being Cleaned
                            </Typography>
                            <Typography variant="h6" color="info.main">
                              {formatNumber(data.maintenance?.being_cleaned || 0)}
                            </Typography>
                          </CardContent>
                        </StatCard>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Analytics - {unit.replace('_', ' ').toUpperCase()}
              </Typography>
              <Typography color="textSecondary">
                Analytics data will be displayed here based on the selected unit.
              </Typography>
            </CardContent>
          </Card>
        );
    }
  };

  return renderAnalyticsContent();
};

// Detailed Unit Report Component
const UnitDetailReport = ({ unit, data, activeTab, onTabChange, selectedDate }) => {
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Summary
        return <UnitSummaryTab unit={unit} data={data} />;
      case 1: // Transactions
        return <UnitTransactionsTab unit={unit} selectedDate={selectedDate} />;
      case 2: // Analytics
        return <UnitAnalyticsTab unit={unit} data={data} />;
      default:
        return null;
    }
  };

  return (
    <Box>
      <AppBar position="static" color="default" elevation={1}>
        <Tabs value={activeTab} onChange={(e, newValue) => onTabChange(newValue)}>
          <Tab label="Summary" />
          <Tab label="Transactions" />
          <Tab label="Analytics" />
        </Tabs>
      </AppBar>
      
      <Box sx={{ mt: 3 }}>
        {renderTabContent()}
      </Box>
    </Box>
  );
};

// Main Dashboard Component
const StatisticsDashboard = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const getHotelCode = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.hotelcode || storedProfile.hotel_code || '';
    } catch (error) {
      console.error('Error getting hotel code:', error);
      return '';
    }
  };

  // API service functions using axios
  const api = {
    getDashboardStatistics: async (date) => {
      const response = await axios.get(`${API_BASE_URL}/hotel/admin/statistics.php`, {
        params: { 
          hotel_code: getHotelCode(), 
          date: date 
        }
      });
      return response.data;
    },

    getUnitTransactions: async (unit, date) => {
      const response = await axios.get(`${API_BASE_URL}/hotel/admin/unit_details.php`, {
        params: { 
          hotel_code: getHotelCode(), 
          unit: unit,
          date: date 
        }
      });
      return response.data;
    }
  };

  const loadDashboardData = async (date = selectedDate) => {
    setLoading(true);
    setError('');
    
    try {
      const hotelCode = getHotelCode();
      if (!hotelCode) {
        throw new Error('Hotel code not found');
      }

      const data = await api.getDashboardStatistics(date);
      if (data.success) {
        setDashboardData(data);
      } else {
        throw new Error(data.error || 'Failed to load dashboard data');
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.message);
      showSnackbar('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    loadDashboardData(newDate);
  };

  const handleUnitClick = (unit) => {
    setSelectedUnit(unit);
    setDetailDialogOpen(true);
    setActiveTab(0);
  };

  const getUnitColor = (unit) => {
    const colors = {
      restaurant_bar: 'primary',
      reception: 'secondary',
      facility_management: 'warning',
    };
    return colors[unit] || 'primary';
  };

  const getUnitTitle = (unit) => {
    const titles = {
      restaurant_bar: 'Restaurant & Bar',
      reception: 'Reception',
      facility_management: 'Facility Management',
    };
    return titles[unit] || unit;
  };

  // Print functionality
  const handlePrint = () => {
    const printContent = document.getElementById('dashboard-content');
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Hotel Statistics Dashboard - ${selectedDate}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .print-header { text-align: center; margin-bottom: 30px; }
            .stats-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 20px; }
            .stat-card { border: 1px solid #ddd; padding: 15px; border-radius: 8px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Export CSV functionality
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      showSnackbar('No data to export', 'warning');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Handle values that might contain commas
          return typeof value === 'string' && value.includes(',') 
            ? `"${value}"` 
            : value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${selectedDate}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showSnackbar('CSV exported successfully', 'success');
  };

  const handleExportCSV = () => {
    if (selectedUnit && dashboardData?.units[selectedUnit]) {
      const unitData = dashboardData.units[selectedUnit];
      let exportData = [];

      switch (selectedUnit) {
        case 'restaurant_bar':
          exportData = unitData.order_types || [];
          break;
        case 'reception':
          exportData = unitData.booking_sources || [];
          break;
        case 'facility_management':
          exportData = unitData.facility_categories || [];
          break;
        default:
          exportData = [];
      }

      exportToCSV(exportData, `${selectedUnit}_report`);
    } else {
      showSnackbar('No data available for export', 'warning');
    }
  };

  // Render metrics for each unit
  const renderUnitMetrics = (unit, data) => {
    switch (unit) {
      case 'restaurant_bar':
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Orders</Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatNumber(data.summary?.total_orders)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Revenue</Typography>
              <Typography variant="body1" fontWeight="bold" color="primary.main">
                {formatCurrency(data.summary?.total_revenue)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Completed</Typography>
              <Chip 
                label={formatNumber(data.summary?.completed_orders)} 
                size="small" 
                color="success"
              />
            </Box>
            <ProgressBar 
              variant="determinate" 
              value={data.summary?.total_orders ? 
                (data.summary.completed_orders / data.summary.total_orders * 100) : 0
              } 
            />
          </Box>
        );

      case 'reception':
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Bookings</Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatNumber(data.summary?.total_bookings)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Revenue</Typography>
              <Typography variant="body1" fontWeight="bold" color="secondary.main">
                {formatCurrency(data.summary?.total_revenue)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Occupancy</Typography>
              <Chip 
                label={`${data.occupancy?.total_rooms ? 
                  Math.round((data.occupancy.occupied_rooms / data.occupancy.total_rooms) * 100) : 0}%`} 
                size="small" 
                color="primary"
              />
            </Box>
            <ProgressBar 
              variant="determinate" 
              value={data.occupancy?.total_rooms ? 
                (data.occupancy.occupied_rooms / data.occupancy.total_rooms * 100) : 0
              } 
            />
          </Box>
        );

      case 'facility_management':
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Bookings</Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatNumber(data.summary?.total_bookings)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Revenue</Typography>
              <Typography variant="body1" fontWeight="bold" color="warning.main">
                {formatCurrency(data.summary?.total_revenue)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="textSecondary">Completed</Typography>
              <Chip 
                label={formatNumber(data.summary?.completed_bookings)} 
                size="small" 
                color="success"
              />
            </Box>
            <ProgressBar 
              variant="determinate" 
              value={data.summary?.total_bookings ? 
                (data.summary.completed_bookings / data.summary.total_bookings * 100) : 0
              } 
            />
          </Box>
        );

      default:
        return null;
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/Entri');
  };

  const handleNotificationClick = () => {
    alert('You have new notifications!');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading dashboard data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
        id="dashboard-content"
      >
        {/* Top Navigation */}
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        {/* Header */}
        <Paper elevation={2} sx={{ p: 3, mt: 3, backgroundColor: 'primary.main', color: 'white', borderRadius: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            📊 Hotel Statistics Dashboard
          </Typography>
          <Typography variant="subtitle1">
            Comprehensive overview of all hotel operations and performance metrics
          </Typography>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
            {error}
            <Button onClick={() => loadDashboardData()} sx={{ ml: 2 }} size="small">
              Retry
            </Button>
          </Alert>
        )}

        {/* Date Filter and Controls */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Select Date"
                type="date"
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <CalendarToday color="action" sx={{ mr: 1 }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Tooltip title="Refresh Data">
                  <IconButton onClick={() => loadDashboardData()} color="primary">
                    <Refresh />
                  </IconButton>
                </Tooltip>
                
               
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Overall Summary */}
        {dashboardData?.summary && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Total Revenue
                      </Typography>
                      <Typography variant="h4" color="primary" fontWeight="bold">
                        {formatCurrency(dashboardData.summary.total_revenue)}
                      </Typography>
                    </Box>
                    <AttachMoney sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {formatNumber(dashboardData.summary.total_transactions)} total transactions
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard color="secondary">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Date
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {new Date(dashboardData.summary.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <CalendarToday sx={{ fontSize: 40, color: 'secondary.main', opacity: 0.7 }} />
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    {dashboardData.summary.date === new Date().toISOString().split('T')[0] ? 'Today' : 'Selected Date'}
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard color="success">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Hotel Code
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="success.main">
                        {dashboardData.hotel_code}
                      </Typography>
                    </Box>
                    <Hotel sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Active property
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <StatCard color="info">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Status
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="info.main">
                        Operational
                      </Typography>
                    </Box>
                    <TrendingUp sx={{ fontSize: 40, color: 'info.main', opacity: 0.7 }} />
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    All systems normal
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>
          </Grid>
        )}

        {/* Unit Cards */}
        <Grid container spacing={3}>
          {dashboardData?.units && Object.entries(dashboardData.units).map(([unit, data]) => (
            <Grid item xs={12} md={6} lg={4} key={unit}>
              <DashboardCard onClick={() => handleUnitClick(unit)}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          backgroundColor: `${getUnitColor(unit)}.light`,
                          borderRadius: '12px',
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <UnitIcon unit={unit} />
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {getUnitTitle(unit)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Click for detailed report
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton size="small" sx={{ color: 'primary.main' }}>
                      <Visibility />
                    </IconButton>
                  </Box>

                  {/* Unit-specific metrics */}
                  {renderUnitMetrics(unit, data)}
                </CardContent>
              </DashboardCard>
            </Grid>
          ))}
        </Grid>

        {/* Unit Detail Dialog */}
        <Dialog 
          open={detailDialogOpen} 
          onClose={() => setDetailDialogOpen(false)} 
          maxWidth="lg" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <UnitIcon unit={selectedUnit} />
              <Typography variant="h5">
                {selectedUnit ? getUnitTitle(selectedUnit) : ''} - Detailed Report
              </Typography>
              <Chip 
                label={selectedDate} 
                variant="outlined" 
                size="small" 
                icon={<CalendarToday />}
              />
            </Box>
          </DialogTitle>
          
          <DialogContent dividers>
            {selectedUnit && dashboardData?.units[selectedUnit] && (
              <UnitDetailReport 
                unit={selectedUnit}
                data={dashboardData.units[selectedUnit]}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                selectedDate={selectedDate}
              />
            )}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
            
            <Button variant="outlined" startIcon={<Download />} onClick={handleExportCSV}>
              Export CSV
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StatisticsDashboard;